import { Box, Button, Heading, PreHeader, Stack, Text } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { MdCheck, MdContentCopy, MdOpenInNew } from "react-icons/md";
import { useNavigate } from "react-router-dom";

/**
 * "Inject context into the chat" card on Configure → Embed.
 *
 * Explains the two hidden-context channels (`StrikeUpChat.context.set` for live page
 * context, `openContext` for the opening turn), lists the do / don't rules that matter
 * most, and offers two copyable snippets. The full reasoning lives in the tenant-facing
 * Widget Integration guide §7.1 — this card is the short version with a link.
 */

// Generic invoicing-app examples — deliberately not any real tenant's data.
const PAGE_CONTEXT_SNIPPET = `// Call on every route change and whenever the record / filters change.
// set() replaces the previous value; the AI gets what is staged when the user sends.
window.StrikeUpChat?.context.set(
  [
    "User: Dana Reyes (id usr_8f3a, role: admin)",
    "Page: Invoice details — /invoices/inv_1042",
    "Viewing invoice INV-1042 (id inv_1042), currently editing line items.",
    "Customer: Example Studios (id cust_88)",
    "Status: overdue | Amount: 1,250.00 EUR | Due: 2026-09-01",
  ].join("\\n")
);

// Re-stage after the widget (re)initialises — staged context does not survive a reload.
window.StrikeUpChat?.onReady(() => window.StrikeUpChat?.context.set(buildPageContext()));`;

const OPEN_CONTEXT_SNIPPET = `// One sentence about why THIS button was pressed — not the page description.
// Forgotten after the first reply; keep identity / page state in context.set().
window.StrikeUpChat?.open({
  message: "What is driving the dip in week 36?",
  openContext: "Visitor clicked 'Explain this chart' on the Revenue by week graph, range: last 12 weeks."
});`;

const RULE_KEYS = [
  "identity",
  "idsAndNames",
  "volatile",
  "loading",
  "queryString",
  "unmount",
] as const;

export default function ContextInjectionPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [copiedPage, setCopiedPage] = useState(false);
  const [copiedOpen, setCopiedOpen] = useState(false);

  const copySnippet = async (text: string, setter: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const Snippet = ({
    code,
    copied,
    onCopy,
  }: {
    code: string;
    copied: boolean;
    onCopy: () => void;
  }) => (
    <>
      <Box
        p={3}
        borderRadius="10px"
        borderWidth="1px"
        borderColor="var(--border-default)"
        bg="var(--bg-primary)"
        mb={2}
        overflowX="auto"
      >
        <Text
          as="pre"
          fontSize="xs"
          fontFamily="var(--font-mono)"
          whiteSpace="pre"
          color="var(--text-primary)"
          m={0}
        >
          {code}
        </Text>
      </Box>
      <Button size="sm" intent="secondary" onClick={onCopy}>
        {copied ? <MdCheck /> : <MdContentCopy />}
        {copied ? t("app.common.copied") : t("app.common.copy")}
      </Button>
    </>
  );

  return (
    <Box
      p={4}
      borderRadius="10px"
      borderWidth="1px"
      borderColor="var(--border-default)"
      bg="var(--bg-secondary)"
      mb={4}
    >
      <PreHeader>{t("app.widgets.embedContext.preHeader")}</PreHeader>
      <Heading size="sm" mt={1} mb={2}>
        {t("app.widgets.embedContext.title")}
      </Heading>
      <Text fontSize="sm" color="var(--text-secondary-color)" mb={3}>
        {t("app.widgets.embedContext.description")}
      </Text>

      <Stack gap={2} mb={4}>
        <Text fontSize="sm" color="var(--text-secondary-color)">
          <Text as="span" fontWeight="semibold" color="var(--text-primary)">
            {t("app.widgets.embedContext.pageContextLabel")}
          </Text>{" "}
          —{" "}
          <Text as="code" fontFamily="mono">
            StrikeUpChat.context.set(value)
          </Text>{" "}
          {t("app.widgets.embedContext.pageContextText")}
        </Text>
        <Text fontSize="sm" color="var(--text-secondary-color)">
          <Text as="span" fontWeight="semibold" color="var(--text-primary)">
            {t("app.widgets.embedContext.openContextLabel")}
          </Text>{" "}
          —{" "}
          <Text as="code" fontFamily="mono">
            openContext
          </Text>{" "}
          {t("app.widgets.embedContext.openContextText")}
        </Text>
      </Stack>

      {/* Why volatility costs — the one fact that makes the rules below obvious */}
      <Box
        p={3}
        borderRadius="8px"
        borderLeftWidth="3px"
        borderLeftColor="var(--brand-primary, #3B82F6)"
        bg="var(--bg-primary)"
        mb={4}
      >
        <Text fontSize="xs" color="var(--text-secondary-color)" m={0}>
          {t("app.widgets.embedContext.costNote")}
        </Text>
      </Box>

      <Text fontSize="sm" fontWeight={500} color="var(--text-primary)" mb={2}>
        {t("app.widgets.embedContext.rulesTitle")}
      </Text>
      <Stack as="ul" gap={1} mb={4} pl={5} style={{ listStyleType: "disc" }}>
        {RULE_KEYS.map((key) => (
          <Text
            as="li"
            key={key}
            fontSize="sm"
            color="var(--text-secondary-color)"
          >
            <Text as="span" fontWeight="semibold" color="var(--text-primary)">
              {t(`app.widgets.embedContext.rules.${key}.do`)}
            </Text>{" "}
            {t(`app.widgets.embedContext.rules.${key}.why`)}
          </Text>
        ))}
      </Stack>

      <Box mb={4}>
        <Text fontSize="sm" fontWeight={500} color="var(--text-primary)" mb={1}>
          {t("app.widgets.embedContext.pageSnippetTitle")}
        </Text>
        <Text fontSize="xs" color="var(--text-secondary-color)" mb={2}>
          {t("app.widgets.embedContext.pageSnippetHint")}
        </Text>
        <Snippet
          code={PAGE_CONTEXT_SNIPPET}
          copied={copiedPage}
          onCopy={() => copySnippet(PAGE_CONTEXT_SNIPPET, setCopiedPage)}
        />
      </Box>

      <Box mb={4}>
        <Text fontSize="sm" fontWeight={500} color="var(--text-primary)" mb={1}>
          {t("app.widgets.embedContext.openSnippetTitle")}
        </Text>
        <Text fontSize="xs" color="var(--text-secondary-color)" mb={2}>
          {t("app.widgets.embedContext.openSnippetHint")}
        </Text>
        <Snippet
          code={OPEN_CONTEXT_SNIPPET}
          copied={copiedOpen}
          onCopy={() => copySnippet(OPEN_CONTEXT_SNIPPET, setCopiedOpen)}
        />
      </Box>

      <Button
        intent="secondary"
        size="sm"
        onClick={() =>
          navigate("/admin/help?tab=technical-docs&doc=widget-integration")
        }
      >
        <MdOpenInNew />
        {t("app.widgets.embedContext.viewGuide")}
      </Button>
    </Box>
  );
}
